import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import request, { getImageSrc } from '../utils/request';
import { useWxShare } from '../hooks/useWxShare';
import './ActivityDetail.css';

function ActivityDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [formData, setFormData] = useState({
    gender: '',
    phone: '',
    wechat: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivityDetail();
  }, [id]);

  // 支付成功后跳回（带 ?paid=1）
  useEffect(() => {
    if (searchParams.get('paid') === '1') {
      fetchActivityDetail();
      setShowSignupForm(false);
      setSelectedTicketType(null);
      alert('报名成功！');
      navigate(`/activity/${id}`, { replace: true });
    }
  }, [searchParams, id, navigate]);

  const fetchActivityDetail = async () => {
    try {
      const res = await request({
        url: `/api/activity/detail/${id}`,
        method: 'GET'
      });
      setActivity(res.data);
    } catch (err) {
      console.error('获取活动详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `¥${(price / 100).toFixed(2)}`;
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'long'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (ticketType = selectedTicketType) => {
    if (!formData.gender || !formData.phone) {
      alert('请选择性别并填写手机号');
      return;
    }

    setSubmitting(true);
    try {
      const res = await request({
        url: '/api/activity/order/create',
        method: 'POST',
        data: {
          activityId: id,
          gender: formData.gender,
          phone: formData.phone,
          wechat: formData.wechat || undefined,
          ticketType: ticketType || 'regular'
        }
      });

      if (res.data.payUrl) {
        // 虎皮椒支付：跳转收银台
        window.location.href = res.data.payUrl;
      } else if (res.data.payParams) {
        // 兼容：调起微信 JSAPI 支付
        if (window.wx && window.wx.requestPayment) {
          window.wx.requestPayment({
            timeStamp: res.data.payParams.timeStamp,
            nonceStr: res.data.payParams.nonceStr,
            package: res.data.payParams.package,
            signType: 'MD5',
            paySign: res.data.payParams.paySign,
            success: () => {
              alert('报名成功！');
              fetchActivityDetail();
              setShowSignupForm(false);
              setSelectedTicketType(null);
            },
            fail: (err) => {
              alert('支付失败：' + (err.errMsg || '未知错误'));
            }
          });
        } else {
          alert('请在微信内打开完成支付');
        }
      }
    } catch (err) {
      alert(err.response?.data?.error || '报名失败');
    } finally {
      setSubmitting(false);
    }
  };

  useWxShare({
    title: activity ? activity.title : 'AI相亲活动',
    desc: activity?.description ? activity.description.slice(0, 80) + (activity.description.length > 80 ? '...' : '') : '线下社交，遇见缘分',
    imgUrl: activity?.cover_image
  });

  if (loading) {
    return <div className="container">加载中...</div>;
  }

  if (!activity) {
    return <div className="container">活动不存在</div>;
  }

  return (
    <div className="container">
      <div className="activity-detail">
        <div className="cover-image">
          {activity.cover_image ? (
            <img
              src={getImageSrc(activity.cover_image)}
              alt={activity.title}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ffe5e5 0%, #fff0f0 100%)' }}>
              <span style={{ fontSize: 64, opacity: 0.4 }}>🎉</span>
            </div>
          )}
          {activity.remainingSlots !== '不限' && activity.remainingSlots < 20 && (
            <span className="remaining-badge">仅剩{activity.remainingSlots}席</span>
          )}
        </div>

        <div className="detail-content">
          <h1>{activity.title}</h1>
          
          <div className="price-section">
            <div className="price-desc">{activity.priceDescription}</div>
          </div>

          {activity.signupClosed && (
            <div className="signup-closed-banner" style={{ padding: '12px 16px', background: '#fff3cd', borderRadius: 8, color: '#856404', marginBottom: 16 }}>
              ⏰ 报名已截止
            </div>
          )}

          {(activity.slotInfo?.earlyBird?.total > 0 || activity.slotInfo?.regular?.total > 0 || activity.total_slots === 0) && activity.slotInfo && (
            <div className="slot-section">
              {activity.slotInfo.earlyBird?.total > 0 && (
                <div className="slot-card">
                  <div className="slot-header">
                    <span className="slot-label">早鸟票</span>
                    <span className="slot-count">{activity.slotInfo.earlyBird.used}/{activity.slotInfo.earlyBird.total}</span>
                  </div>
                  <div className="slot-progress-wrap">
                    <div 
                      className="slot-progress" 
                      style={{ width: `${(activity.slotInfo.earlyBird.used / activity.slotInfo.earlyBird.total) * 100}%` }}
                    />
                  </div>
                  <div className="slot-footer">
                    <span className="slot-price">{formatPrice(activity.earlyBirdPrice || activity.currentPrice)}</span>
                    {activity.signupClosed ? (
                      <span style={{ color: '#999', fontSize: 14 }}>报名已截止</span>
                    ) : (
                      <button
                        className={`btn ${activity.slotInfo.earlyBird.soldOut ? 'btn-disabled' : 'btn-primary'}`}
                        disabled={activity.slotInfo.earlyBird.soldOut}
                        onClick={() => { setSelectedTicketType('early_bird'); setShowSignupForm(true); }}
                      >
                        {activity.slotInfo.earlyBird.soldOut ? '已售罄' : `报名早鸟票`}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {(activity.slotInfo.regular?.total > 0 || activity.total_slots === 0) && (
                <div className="slot-card">
                  <div className="slot-header">
                    <span className="slot-label">正常票</span>
                    <span className="slot-count">
                      {activity.slotInfo.regular.total === 0 ? `${activity.slotInfo.regular.used}/不限` : `${activity.slotInfo.regular.used}/${activity.slotInfo.regular.total}`}
                    </span>
                  </div>
                  {activity.slotInfo.regular.total > 0 && (
                    <div className="slot-progress-wrap">
                      <div 
                        className="slot-progress" 
                        style={{ width: `${(activity.slotInfo.regular.used / activity.slotInfo.regular.total) * 100}%` }}
                      />
                    </div>
                  )}
                  <div className="slot-footer">
                    <span className="slot-price">{formatPrice(activity.regularPrice || activity.originalPrice)}</span>
                    {activity.signupClosed ? (
                      <span style={{ color: '#999', fontSize: 14 }}>报名已截止</span>
                    ) : (
                      <button
                        className={`btn ${activity.slotInfo.regular.soldOut ? 'btn-disabled' : 'btn-primary'}`}
                        disabled={activity.slotInfo.regular.soldOut}
                        onClick={() => { setSelectedTicketType('regular'); setShowSignupForm(true); }}
                      >
                        {activity.slotInfo.regular.soldOut ? '已售罄' : `报名正常票`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="info-section">
            <div className="info-item">
              <span className="label">📅 活动时间</span>
              <span className="value">{formatDateTime(activity.start_time)}</span>
            </div>
            
            <div className="info-item">
              <span className="label">📍 活动地点</span>
              <span className="value">{activity.location_name}</span>
              <span className="sub-value">{activity.location_address}</span>
            </div>
            
            {activity.location_guide && (
              <div className="info-item">
                <span className="label">🚗 交通指引</span>
                <span className="value">{activity.location_guide}</span>
              </div>
            )}
            
            {!activity.slotInfo && (
              <div className="info-item">
                <span className="label">👥 剩余名额</span>
                <span className="value">{activity.remainingSlots}</span>
              </div>
            )}
            {activity.signupProgress && (
              <div className="info-item">
                <span className="label">📊 报名进度</span>
                <span className="value">
                  男 {activity.signupProgress.male || 0} 人 · 女 {activity.signupProgress.female || 0} 人
                </span>
              </div>
            )}
          </div>

          {activity.description && (
            <div className="description-section">
              <h3>活动详情</h3>
              <div
                className="activity-description-html"
                dangerouslySetInnerHTML={{ __html: activity.description }}
                style={{ lineHeight: 1.7 }}
              />
            </div>
          )}

          {activity.images && activity.images.length > 0 && (
            <div className="images-section">
              <h3>活动图片</h3>
              <div className="image-grid">
                {activity.images.map((img, idx) => (
                  <img key={idx} src={img} alt={`活动图${idx+1}`} />
                ))}
              </div>
            </div>
          )}

          {activity.userRegistration ? (
            <div className="already-registered">
              <p>✅ 您已报名该活动</p>
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/my-registrations')}
              >
                查看我的报名
              </button>
            </div>
          ) : showSignupForm ? (
            <div className="signup-form">
                <h3>填写报名信息 {selectedTicketType === 'early_bird' ? '（早鸟票）' : '（正常票）'}</h3>
                
                <div className="form-group">
                  <label>性别 <span style={{ color: 'red' }}>*</span></label>
                  <select name="gender" value={formData.gender} onChange={handleInputChange} required>
                    <option value="">请选择</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>手机号 <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="用于联系"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>微信号</label>
                  <input
                    type="text"
                    name="wechat"
                    value={formData.wechat}
                    onChange={handleInputChange}
                    placeholder="选填"
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => { setShowSignupForm(false); setSelectedTicketType(null); }}
                  >
                    取消
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleSubmit(selectedTicketType)}
                    disabled={submitting}
                  >
                    {submitting ? '提交中...' : `支付 ${formatPrice(selectedTicketType === 'early_bird' ? (activity.earlyBirdPrice || activity.currentPrice) : (activity.regularPrice || activity.originalPrice))}`}
                  </button>
                </div>
              </div>
          ) : !activity.signupClosed && (
            activity.slotInfo && (activity.slotInfo.earlyBird?.total > 0 || activity.slotInfo.regular?.total > 0 || activity.total_slots === 0) ? null : (
              <button 
                className="btn btn-primary signup-btn"
                onClick={() => { setSelectedTicketType('regular'); setShowSignupForm(true); }}
              >
                立即报名
              </button>
            )
          )}
        </div>
      </div>

      <div className="footer-nav">
        <button 
          className="btn btn-text"
          onClick={() => navigate('/activities')}
        >
          ← 返回列表
        </button>
        <button 
          className="btn btn-text"
          onClick={() => navigate('/')}
        >
          首页
        </button>
      </div>
    </div>
  );
}

export default ActivityDetail;